import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, CheckSquare, RefreshCw, X } from 'lucide-react'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { he } from 'date-fns/locale'
import { orgPulseApi, type TrialManagementOrg } from '@/api/orgPulse'
import { subscribersApi } from '@/api/subscribers'

const ZONE = 'Asia/Jerusalem'
const LINE = '#2a2d3e'
const MUTED = '#9ca3af'
const CARD = { background: '#1a1d27', border: `1px solid ${LINE}` } as const

type ExtensionChoice = 14 | 30 | 'year' | 'date'

function dateText(value: string | null): string {
  return value ? formatInTimeZone(value, ZONE, 'dd.MM.yyyy', { locale: he }) : 'ללא תאריך'
}
function stampText(value: string): string {
  return formatInTimeZone(value, ZONE, 'dd.MM.yyyy HH:mm', { locale: he })
}

function extensionInput(choice: ExtensionChoice, date: string, note: string) {
  if (choice === 14 || choice === 30) return { days: choice, until: null, note }
  const localDate = choice === 'year' ? '2026-12-31' : date
  if (!localDate) throw new Error('יש לבחור תאריך')
  return {
    days: null,
    until: fromZonedTime(`${localDate}T23:59:59.999`, ZONE).toISOString(),
    note,
  }
}

function ExtendDialog({ rows, onClose, onDone }: {
  rows: TrialManagementOrg[]
  onClose: () => void
  onDone: () => void
}) {
  const [choice, setChoice] = useState<ExtensionChoice>(14)
  const [date, setDate] = useState('')
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  const mutation = useMutation({
    mutationFn: async () => {
      const input = extensionInput(choice, date, reason.trim())
      const completed = [] as string[]
      for (const row of rows) {
        await subscribersApi.extendTrial(row.organization_id, input)
        completed.push(row.organization_id)
      }
      return completed
    },
    onSuccess: onDone,
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,.78)' }}>
      <section dir="rtl" className="w-full max-w-lg rounded-2xl p-5 text-white" style={CARD} role="dialog" aria-modal="true" aria-label="הארכת התנסות">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">הארכת התנסות</h2>
            <p className="mt-1 text-sm" style={{ color: MUTED }}>{rows.length === 1 ? rows[0].name : `${rows.length} ארגונים נבחרו`}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="סגירה"><X size={18} /></button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {([[14, '+14 ימים'], [30, '+30 ימים'], ['year', 'סוף השנה'], ['date', 'תאריך']] as const).map(([value, label]) => (
            <button key={value} type="button" onClick={() => setChoice(value)} className="rounded-lg px-3 py-2 text-sm"
              style={{ border: `1px solid ${choice === value ? '#818cf8' : LINE}`, background: choice === value ? '#1B2A48' : 'transparent' }}>
              {label}
            </button>
          ))}
        </div>

        {choice === 'date' ? (
          <label className="mt-4 grid gap-1 text-sm">תאריך סיום
            <input type="date" value={date} onChange={(event) => setDate(event.target.value)} className="rounded-lg px-3 py-2 text-white"
              style={{ background: '#111827', border: `1px solid ${LINE}`, colorScheme: 'dark' }} />
          </label>
        ) : null}

        <label className="mt-4 grid gap-1 text-sm">סיבה (אופציונלי)
          <input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="למשל: הארכה אישית של ניצן"
            className="rounded-lg px-3 py-2 text-white" style={{ background: '#111827', border: `1px solid ${LINE}` }} />
        </label>

        <label className="mt-4 flex items-start gap-2 rounded-lg p-3 text-sm" style={{ border: `1px solid ${LINE}` }}>
          <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} className="mt-1" />
          <span>אני מאשר/ת להאריך את ההתנסות עבור {rows.length === 1 ? rows[0].name : `${rows.length} הארגונים שנבחרו`}.</span>
        </label>

        {mutation.isError ? <p className="mt-3 text-sm text-red-300">{mutation.error instanceof Error ? mutation.error.message : 'ההארכה נכשלה'}</p> : null}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm" style={{ border: `1px solid ${LINE}` }}>ביטול</button>
          <button type="button" disabled={!confirmed || mutation.isPending || (choice === 'date' && !date)} onClick={() => mutation.mutate()}
            className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold disabled:opacity-40">
            {mutation.isPending ? 'מאריך…' : `הארך ${rows.length > 1 ? `${rows.length} התנסויות` : ''}`}
          </button>
        </div>
      </section>
    </div>
  )
}

export default function TrialManagementView() {
  const [includeTest, setIncludeTest] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [dialogRows, setDialogRows] = useState<TrialManagementOrg[]>([])
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ['trial-management', includeTest],
    queryFn: () => orgPulseApi.trialManagement(includeTest),
    retry: false,
  })
  const rows = query.data ?? []
  const selectedRows = rows.filter((row) => selected.has(row.organization_id))
  const counters = useMemo(() => ({
    trial: rows.filter((row) => ['trial', 'trialing'].includes(row.subscription_state)).length,
    seven: rows.filter((row) => row.trial_days_left != null && row.trial_days_left >= 0 && row.trial_days_left <= 7).length,
    followUp: rows.filter((row) => row.deal_stage === 'follow_up').length,
    noDate: rows.filter((row) => !row.trial_end).length,
  }), [rows])

  const toggle = (id: string) => setSelected((current) => {
    const next = new Set(current)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  const finish = async () => {
    setDialogRows([])
    setSelected(new Set())
    await queryClient.invalidateQueries({ queryKey: ['trial-management'] })
  }

  return (
    <section className="grid gap-4" data-trial-management>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">ניהול התנסויות</h2>
          <p className="text-sm" style={{ color: MUTED }}>כל התנסויות JumpStart, במקום אחד</p>
        </div>
        <div className="flex items-center gap-3 text-sm" style={{ color: MUTED }}>
          <label className="inline-flex items-center gap-2"><input type="checkbox" checked={includeTest} onChange={(event) => setIncludeTest(event.target.checked)} />הצג בדיקות</label>
          <button type="button" onClick={() => void query.refetch()} className="inline-flex items-center gap-1 rounded-lg px-2 py-1" style={{ border: `1px solid ${LINE}` }}>
            <RefreshCw size={14} className={query.isFetching ? 'animate-spin' : ''} /> רענון
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          ['בהתנסות', counters.trial],
          ['נגמרות ב־7 ימים', counters.seven],
          ['במעקב', counters.followUp],
          ['ללא תאריך', counters.noDate],
        ].map(([label, value]) => <div key={label} className="rounded-xl p-3" style={CARD}><b className="block text-2xl text-white">{value}</b><span className="text-xs" style={{ color: MUTED }}>{label}</span></div>)}
      </div>

      {selectedRows.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl px-3 py-2" style={{ background: '#1B2A48', border: '1px solid #6366f1' }}>
          <span className="text-sm text-white"><CheckSquare size={15} className="ml-1 inline" />נבחרו {selectedRows.length}</span>
          <button type="button" onClick={() => setDialogRows(selectedRows)} className="rounded-lg bg-indigo-500 px-3 py-1.5 text-sm text-white">הארך מסומנים</button>
        </div>
      ) : null}

      {query.isLoading ? <div className="py-16 text-center" style={{ color: MUTED }}>טוען התנסויות…</div> : null}
      {query.isError ? <div className="rounded-xl p-5 text-center text-red-300" style={CARD}>לא הצלחנו לטעון את ההתנסויות. נסו לרענן.</div> : null}
      {!query.isLoading && !query.isError ? (
        <div className="w-full overflow-x-auto rounded-xl" style={{ border: `1px solid ${LINE}` }}>
          <table className="w-full min-w-[1050px] border-collapse text-sm text-white">
            <thead><tr>{['', 'ארגון', 'בעלים / טלפון', 'מסלול', 'סיום התנסות', 'ימים', 'שלב CRM', 'כרטיס שמור', 'כניסה אחרונה', ''].map((label, index) =>
              <th key={`${label}-${index}`} className="px-3 py-2 text-right text-xs font-medium" style={{ color: MUTED, borderBottom: `1px solid ${LINE}` }}>{label}</th>)}</tr></thead>
            <tbody>{rows.map((row) => (
              <tr key={row.organization_id} data-trial-org={row.name} style={{ background: selected.has(row.organization_id) ? '#1B2A48' : 'transparent' }}>
                <td className="px-3 py-3" style={{ borderBottom: `1px solid ${LINE}` }}><input type="checkbox" checked={selected.has(row.organization_id)} onChange={() => toggle(row.organization_id)} aria-label={`בחר ${row.name}`} /></td>
                <td className="px-3 py-3 font-semibold" style={{ borderBottom: `1px solid ${LINE}` }}>{row.name}</td>
                <td className="px-3 py-3" style={{ borderBottom: `1px solid ${LINE}` }}><div>{row.owner_name ?? '—'}</div><div className="text-xs" dir="ltr" style={{ color: MUTED }}>{row.owner_phone ?? '—'}</div></td>
                <td className="px-3 py-3" style={{ borderBottom: `1px solid ${LINE}` }}>{row.plan_name || row.plan_code || '—'}</td>
                <td className="px-3 py-3" style={{ borderBottom: `1px solid ${LINE}` }}><div>{dateText(row.trial_end)}</div>{row.latest_extension ? <div className="mt-1 text-xs text-indigo-300">הוארך ע״י {row.latest_extension.extended_by_email ?? 'מנהל'} ב־{stampText(row.latest_extension.created_at)}</div> : null}</td>
                <td className={`px-3 py-3 font-semibold ${row.trial_days_left != null && row.trial_days_left < 0 ? 'text-red-300' : ''}`} style={{ borderBottom: `1px solid ${LINE}` }}>{row.trial_days_left ?? '—'}</td>
                <td className="px-3 py-3" style={{ borderBottom: `1px solid ${LINE}` }}>{row.deal_stage_label}</td>
                <td className="px-3 py-3" style={{ borderBottom: `1px solid ${LINE}` }}>{row.has_saved_card ? 'כן' : 'לא'}</td>
                <td className="px-3 py-3" style={{ borderBottom: `1px solid ${LINE}` }}>{row.last_login_at ? stampText(row.last_login_at) : 'לא נכנס'}</td>
                <td className="px-3 py-3" style={{ borderBottom: `1px solid ${LINE}` }}><button type="button" onClick={() => setDialogRows([row])} className="inline-flex items-center gap-1 rounded-lg bg-indigo-500 px-3 py-1.5 text-sm"><CalendarDays size={14} />הארך</button></td>
              </tr>
            ))}</tbody>
          </table>
          {rows.length === 0 ? <p className="py-10 text-center text-sm" style={{ color: MUTED }}>אין התנסויות להצגה</p> : null}
        </div>
      ) : null}
      {dialogRows.length > 0 ? <ExtendDialog rows={dialogRows} onClose={() => setDialogRows([])} onDone={() => void finish()} /> : null}
    </section>
  )
}

